import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
} from '@mui/icons-material';
import { customerApi } from '../services/api';
import { Customer, CustomerFormData, PaginationInfo, PaginationParams } from '../types';
import Pagination from '../components/Pagination';

const Customers: React.FC = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [formData, setFormData] = useState<CustomerFormData>({
    name: '',
    phone: '',
    email: '',
    address: '',
  });
  
  // Pagination state
  const [pagination, setPagination] = useState<PaginationInfo>({
    current_page: 1,
    per_page: 10,
    total_records: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });
  const [paginationParams, setPaginationParams] = useState<PaginationParams>({
    page: 1,
    limit: 10,
    search: '',
    sort_by: 'created_at',
    sort_order: 'DESC',
  });

  const loadCustomers = useCallback(async (showLoading = true, params?: PaginationParams) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);
      const response = await customerApi.getAll(params || paginationParams);
      if (response.status === 'success' && response.data) {
        setCustomers(response.data);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        setError(response.message || 'Failed to load customers');
      }
    } catch (err) {
      setError('Failed to load customers');
      console.error('Load customers error:', err);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, []); // Remove paginationParams dependency to prevent infinite loop

  const searchCustomers = useCallback(async () => {
    const searchParams = {
      ...paginationParams,
      search: searchQuery,
      page: 1, // Reset to first page when searching
    };
    setPaginationParams(searchParams);
    
    try {
      setLoading(true);
      setError(null);
      const response = await customerApi.getAll(searchParams);
      if (response.status === 'success' && response.data) {
        setCustomers(response.data);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        setError(response.message || 'Failed to search customers');
      }
    } catch (err) {
      setError('Failed to search customers');
      console.error('Search customers error:', err);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, paginationParams]);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    const newParams = { ...paginationParams, page };
    setPaginationParams(newParams);
    
    // Load customers with new page
    loadCustomers(true, newParams);
  };

  const handlePageSizeChange = (pageSize: number) => {
    const newParams = { ...paginationParams, page: 1, limit: pageSize };
    setPaginationParams(newParams);
    
    // Load customers with new page size
    loadCustomers(true, newParams);
  };

  // Sort functionality can be added later if needed

  useEffect(() => {
    loadCustomers();
  }, []); // Only run once on mount

  // Removed auto-search - only search when Search button is clicked

  const handleOpenDialog = (customer?: Customer) => {
    if (customer) {
      setEditingCustomer(customer);
      setFormData({
        name: customer.name,
        phone: customer.phone,
        email: customer.email || '',
        address: customer.address || '',
      });
    } else {
      setEditingCustomer(null);
      setFormData({
        name: '',
        phone: '',
        email: '',
        address: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCustomer(null);
    setError(null); // Clear error when closing dialog
    setSuccess(null); // Clear success message when closing dialog
    setSubmitting(false); // Reset submitting state
    setFormData({
      name: '',
      phone: '',
      email: '',
      address: '',
    });
  };

  const handleInputChange = (field: keyof CustomerFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true); // Start submitting
      setError(null); // Clear any previous errors
      setSuccess(null); // Clear any previous success messages
      
      if (editingCustomer) {
        const response = await customerApi.update(editingCustomer.id, formData);
        if (response.status === 'success') {
          setSuccess('Customer updated successfully!');
          // Show success message in dialog for 1.5 seconds before closing
          setTimeout(async () => {
            await loadCustomers(false); // Don't show loading spinner
            handleCloseDialog();
            // Auto-clear success message after 3 seconds
            setTimeout(() => setSuccess(null), 3000);
          }, 1500);
        } else {
          setError(response.message || 'Failed to update customer');
        }
      } else {
        const response = await customerApi.create(formData);
        if (response.status === 'success') {
          setSuccess('Customer created successfully!');
          // Show success message in dialog for 1.5 seconds before closing
          setTimeout(async () => {
            await loadCustomers(false); // Don't show loading spinner
            handleCloseDialog();
            // Auto-clear success message after 3 seconds
            setTimeout(() => setSuccess(null), 3000);
          }, 1500);
        } else {
          setError(response.message || 'Failed to create customer');
        }
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save customer';
      setError(errorMessage);
      console.error('Save customer error:', err);
    } finally {
      setSubmitting(false); // Stop submitting
    }
  };

  const handleDelete = async (customerId: number) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        const response = await customerApi.delete(customerId);
        if (response.status === 'success') {
          await loadCustomers(false); // Don't show loading spinner for delete
        }
      } catch (err) {
        setError('Failed to delete customer');
        console.error('Delete customer error:', err);
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Search Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center">
          <SearchIcon color="action" />
          <TextField
            placeholder="Search customers by name or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ flexGrow: 1, minWidth: 200 }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                searchCustomers();
              }
            }}
          />
          <Button
            variant="contained"
            onClick={searchCustomers}
            startIcon={<SearchIcon />}
            size="small"
          >
            Search
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            size="small"
            color="success"
          >
            Add Customer
          </Button>
          {searchQuery && (
            <Button
              variant="outlined"
              onClick={() => {
                setSearchQuery('');
                setPaginationParams({
                  page: 1,
                  limit: 10,
                  search: '',
                  sort_by: 'created_at',
                  sort_order: 'DESC',
                });
                loadCustomers();
              }}
              size="small"
            >
              Clear
            </Button>
          )}
        </Box>
      </Paper>

      {/* Customers Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Address</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customers.map((customer) => (
                <TableRow 
                  key={customer.id}
                  onClick={() => navigate(`/customers/${customer.id}`)}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {customer.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <PhoneIcon color="action" fontSize="small" />
                      {customer.phone}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {customer.email ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <EmailIcon color="action" fontSize="small" />
                        {customer.email}
                      </Box>
                    ) : (
                      <Typography color="textSecondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {customer.address ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <LocationOnIcon color="action" fontSize="small" />
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {customer.address}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography color="textSecondary">-</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {customers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="textSecondary">
                      {searchQuery ? 'No customers found matching your search' : 'No customers found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <Pagination
          pagination={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </Paper>

      {/* Add/Edit Customer Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Name *"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Phone *"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              fullWidth
              type="email"
            />
            <TextField
              label="Address"
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.name || !formData.phone || submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : null}
          >
            {submitting 
              ? (editingCustomer ? 'Updating...' : 'Adding...') 
              : (editingCustomer ? 'Update' : 'Add')
            }
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Customers; 